import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
} from '../legacy-ball/table-management/designTokens';

// ─── Interfaces ───────────────────────────────────────────────

interface SnapshotSummary {
  totalOrders: number;
  totalTickets: number;
  totalGuestRecords: number;
  provisionedGuests: number;
  unlinkedGuests: number;
  ticketsWithUserAccounts: number;
  syntheticAccountsCount: number;
}

interface SnapshotDelta {
  ordersAdded: number;
  ticketsAdded: number;
  guestRecordsAdded: number;
  provisionedGuestsAdded: number;
  syntheticAccountsAdded: number;
}

interface LinkedUserSnapshot {
  userId: string;
  email: string;
  fullName: string;
  role: string;
  isSynthetic: boolean;
}

interface TicketSnapshot {
  id: string;
  ticketNumber: number;
  tierName: string;
  status: string;
  guestName: string | null;
  linkedUser: LinkedUserSnapshot | null;
}

interface OrderSnapshot {
  id: string;
  referenceId: string;
  purchaserName: string;
  purchaserEmail: string;
  ticketCount: number;
  tierName: string;
  status: string;
  creationType: string;
  tickets: TicketSnapshot[];
}

interface GuestRecordSnapshot {
  id: string;
  fullName: string;
  ticketNumber: string | null;
  chapterAffiliation: string | null;
  rsvpStatus: string;
  paymentStatus: string;
  isProvisioned: boolean;
  linkedUser: LinkedUserSnapshot | null;
}

interface SnapshotDetail {
  orders: OrderSnapshot[];
  guestRecords: GuestRecordSnapshot[];
}

interface SnapshotComparison {
  uploadId: string;
  fileName: string;
  before: {
    summary: SnapshotSummary;
    takenAt: string;
  } | null;
  after: {
    summary: SnapshotSummary;
    takenAt: string;
  } | null;
  delta: SnapshotDelta | null;
  beforeDetail?: SnapshotDetail;
  afterDetail?: SnapshotDetail;
}

// ─── Props ────────────────────────────────────────────────────

interface SnapshotViewerModalProps {
  uploadId: string;
  fileName: string;
  isOpen: boolean;
  onClose: () => void;
}

// ─── Styles ───────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  content: {
    backgroundColor: BG_CARD,
    borderRadius: '12px',
    padding: '28px',
    width: '780px',
    maxWidth: '92vw',
    maxHeight: '85vh',
    overflowY: 'auto',
    position: 'relative',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: PRIMARY,
    margin: 0,
  },
  subtitle: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    margin: '4px 0 0 0',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#666',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '3rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  errorContainer: {
    padding: '2rem',
    textAlign: 'center',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  section: {
    marginTop: '1.25rem',
  },
  sectionTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.5rem',
    marginTop: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '0',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    overflow: 'hidden',
  },
  gridHeader: {
    padding: '0.5rem 0.75rem',
    backgroundColor: BG_PAGE,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: TEXT_DARK,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  gridCell: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    color: TEXT_DARK,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  gridLabel: {
    padding: '0.4rem 0.75rem',
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    fontWeight: 500,
  },
  timestamp: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
    marginTop: '0.35rem',
  },
  deltaSection: {
    marginTop: '1.25rem',
    padding: '0.75rem 1rem',
    backgroundColor: BG_PAGE,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  deltaSectionTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.5rem',
    marginTop: 0,
  },
  deltaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.25rem 0',
    fontSize: '0.8rem',
  },
  deltaLabel: {
    color: TEXT_MUTED,
  },
  deltaValue: {
    color: '#16a34a',
    fontWeight: 600,
  },
  deltaUnavailable: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
  },
  expandableHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    padding: '0.6rem 0.75rem',
    backgroundColor: BG_PAGE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginTop: '0.75rem',
    width: '100%',
    textAlign: 'left',
  },
  detailTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.75rem',
    marginTop: '0.5rem',
  },
  detailTh: {
    padding: '0.4rem 0.5rem',
    textAlign: 'left',
    backgroundColor: BG_PAGE,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    fontWeight: 600,
    color: TEXT_DARK,
    whiteSpace: 'nowrap',
  },
  detailTd: {
    padding: '0.35rem 0.5rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
    verticalAlign: 'top',
  },
  nestedTicket: {
    paddingLeft: '1.5rem',
    fontSize: '0.7rem',
    color: TEXT_MUTED,
  },
  provisionBadge: {
    display: 'inline-block',
    padding: '0.1rem 0.4rem',
    borderRadius: '9999px',
    fontSize: '0.65rem',
    fontWeight: 500,
  },
};

// ─── Component ────────────────────────────────────────────────

export function SnapshotViewerModal({ uploadId, fileName, isOpen, onClose }: SnapshotViewerModalProps) {
  const [data, setData] = useState<SnapshotComparison | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [detailData, setDetailData] = useState<SnapshotComparison | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [orderDetailsExpanded, setOrderDetailsExpanded] = useState(false);
  const [guestDetailsExpanded, setGuestDetailsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const fetchSnapshots = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      const response = await apiClient<SnapshotComparison>(
        `/order-import/${uploadId}/snapshots`,
      );
      setData(response);
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [uploadId]);

  const fetchDetailData = useCallback(async () => {
    if (detailData) return;
    setDetailLoading(true);
    try {
      const response = await apiClient<SnapshotComparison>(
        `/order-import/${uploadId}/snapshots?detail=true`,
      );
      setDetailData(response);
    } catch {
      // Detail fetch failed — keep detail sections empty
    } finally {
      setDetailLoading(false);
    }
  }, [uploadId, detailData]);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      fetchSnapshots();
    } else {
      // Reset state when closed
      setData(null);
      setDetailData(null);
      setOrderDetailsExpanded(false);
      setGuestDetailsExpanded(false);
    }
  }, [isOpen, fetchSnapshots]);

  // Focus management
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.focus();
    }
    return () => {
      if (!isOpen && previousFocusRef.current?.focus) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleExpandOrders = () => {
    if (!orderDetailsExpanded && !detailData) {
      fetchDetailData();
    }
    setOrderDetailsExpanded(!orderDetailsExpanded);
  };

  const handleExpandGuests = () => {
    if (!guestDetailsExpanded && !detailData) {
      fetchDetailData();
    }
    setGuestDetailsExpanded(!guestDetailsExpanded);
  };

  if (!isOpen) return null;

  const formatTimestamp = (ts: string): string => {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const summaryLabels: { key: keyof SnapshotSummary; label: string }[] = [
    { key: 'totalOrders', label: 'Total Orders' },
    { key: 'totalTickets', label: 'Total Tickets' },
    { key: 'totalGuestRecords', label: 'Guest Records' },
    { key: 'provisionedGuests', label: 'Provisioned Guests' },
    { key: 'unlinkedGuests', label: 'Unlinked Guests' },
    { key: 'ticketsWithUserAccounts', label: 'Tickets w/ Accounts' },
    { key: 'syntheticAccountsCount', label: 'Synthetic Accounts' },
  ];

  const deltaLabels: { key: keyof SnapshotDelta; label: string }[] = [
    { key: 'ordersAdded', label: 'Orders Added' },
    { key: 'ticketsAdded', label: 'Tickets Added' },
    { key: 'guestRecordsAdded', label: 'Guest Records Added' },
    { key: 'provisionedGuestsAdded', label: 'Provisioned Guests Added' },
    { key: 'syntheticAccountsAdded', label: 'Synthetic Accounts Added' },
  ];

  const renderComparisonGrid = () => {
    if (!data) return null;

    return (
      <div style={styles.section}>
        <h4 style={styles.sectionTitle}>Before / After Comparison</h4>
        <div style={styles.grid}>
          {/* Header row */}
          <div style={styles.gridHeader}>Metric</div>
          <div style={styles.gridHeader}>Before</div>
          <div style={styles.gridHeader}>After</div>

          {/* Data rows */}
          {summaryLabels.map(({ key, label }) => (
            <React.Fragment key={key}>
              <div style={styles.gridLabel}>{label}</div>
              <div style={styles.gridCell}>
                {data.before ? data.before.summary[key] : '—'}
              </div>
              <div style={styles.gridCell}>
                {data.after ? data.after.summary[key] : '—'}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Timestamps */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
          <div style={styles.timestamp}>
            {data.before ? `Before: ${formatTimestamp(data.before.takenAt)}` : ''}
          </div>
          <div style={styles.timestamp}>
            {data.after ? `After: ${formatTimestamp(data.after.takenAt)}` : ''}
          </div>
        </div>
      </div>
    );
  };

  const renderDelta = () => {
    if (!data) return null;

    return (
      <div style={styles.deltaSection}>
        <h4 style={styles.deltaSectionTitle}>Changes</h4>
        {data.delta ? (
          deltaLabels.map(({ key, label }) => {
            const value = data.delta![key];
            return (
              <div key={key} style={styles.deltaRow}>
                <span style={styles.deltaLabel}>{label}</span>
                <span style={styles.deltaValue}>
                  {value > 0 ? `+${value}` : value}
                </span>
              </div>
            );
          })
        ) : (
          <span style={styles.deltaUnavailable}>Delta unavailable</span>
        )}
      </div>
    );
  };

  const renderOrderDetails = () => {
    const detail = detailData?.afterDetail || detailData?.beforeDetail;
    if (!detail || !detail.orders || detail.orders.length === 0) {
      return <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, margin: '0.5rem 0' }}>No order data available.</p>;
    }

    return (
      <table style={styles.detailTable}>
        <thead>
          <tr>
            <th style={styles.detailTh}>Reference ID</th>
            <th style={styles.detailTh}>Purchaser</th>
            <th style={styles.detailTh}>Tickets</th>
            <th style={styles.detailTh}>Tier</th>
            <th style={styles.detailTh}>Status</th>
          </tr>
        </thead>
        <tbody>
          {detail.orders.map((order) => (
            <React.Fragment key={order.id}>
              <tr>
                <td style={styles.detailTd}>{order.referenceId}</td>
                <td style={styles.detailTd}>{order.purchaserName}</td>
                <td style={styles.detailTd}>{order.ticketCount}</td>
                <td style={styles.detailTd}>{order.tierName}</td>
                <td style={styles.detailTd}>{order.status}</td>
              </tr>
              {order.tickets.map((ticket) => (
                <tr key={ticket.id}>
                  <td colSpan={5} style={styles.detailTd}>
                    <div style={styles.nestedTicket}>
                      🎫 TKT-{String(ticket.ticketNumber).padStart(3, '0')} — {ticket.tierName} — {ticket.status}
                      {ticket.guestName && ` — ${ticket.guestName}`}
                      {ticket.linkedUser && (
                        <span> ({ticket.linkedUser.email}{ticket.linkedUser.isSynthetic ? ', synthetic' : ''})</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  };

  const renderGuestDetails = () => {
    const detail = detailData?.afterDetail || detailData?.beforeDetail;
    if (!detail || !detail.guestRecords || detail.guestRecords.length === 0) {
      return <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, margin: '0.5rem 0' }}>No guest record data available.</p>;
    }

    return (
      <table style={styles.detailTable}>
        <thead>
          <tr>
            <th style={styles.detailTh}>Full Name</th>
            <th style={styles.detailTh}>Ticket #</th>
            <th style={styles.detailTh}>Chapter</th>
            <th style={styles.detailTh}>RSVP</th>
            <th style={styles.detailTh}>Provisioned</th>
          </tr>
        </thead>
        <tbody>
          {detail.guestRecords.map((guest) => (
            <tr key={guest.id}>
              <td style={styles.detailTd}>{guest.fullName}</td>
              <td style={styles.detailTd}>{guest.ticketNumber || '—'}</td>
              <td style={styles.detailTd}>{guest.chapterAffiliation || '—'}</td>
              <td style={styles.detailTd}>{guest.rsvpStatus}</td>
              <td style={styles.detailTd}>
                <span
                  style={{
                    ...styles.provisionBadge,
                    backgroundColor: guest.isProvisioned ? '#dcfce7' : '#f3f4f6',
                    color: guest.isProvisioned ? '#166534' : '#6b7280',
                  }}
                >
                  {guest.isProvisioned ? 'Yes' : 'No'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderExpandableDetails = () => {
    return (
      <div style={styles.section}>
        {/* Order Details */}
        <button
          type="button"
          style={styles.expandableHeader}
          onClick={handleExpandOrders}
          aria-expanded={orderDetailsExpanded}
        >
          <span>{orderDetailsExpanded ? '▼' : '▶'}</span>
          <span>Order Details</span>
          {detailLoading && <span style={{ fontSize: '0.7rem', color: TEXT_MUTED }}>(loading...)</span>}
        </button>
        {orderDetailsExpanded && renderOrderDetails()}

        {/* Guest Record Details */}
        <button
          type="button"
          style={styles.expandableHeader}
          onClick={handleExpandGuests}
          aria-expanded={guestDetailsExpanded}
        >
          <span>{guestDetailsExpanded ? '▼' : '▶'}</span>
          <span>Guest Record Details</span>
          {detailLoading && <span style={{ fontSize: '0.7rem', color: TEXT_MUTED }}>(loading...)</span>}
        </button>
        {guestDetailsExpanded && renderGuestDetails()}
      </div>
    );
  };

  return (
    <div
      style={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="snapshot-modal-title"
      onKeyDown={handleKeyDown}
    >
      <div ref={contentRef} style={styles.content} tabIndex={-1}>
        <div style={styles.header}>
          <div>
            <h3 id="snapshot-modal-title" style={styles.title}>
              📸 Import Snapshots
            </h3>
            <p style={styles.subtitle}>{fileName}</p>
          </div>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            ✕
          </button>
        </div>

        {isLoading && (
          <div style={styles.loadingContainer}>
            <span>⏳</span>
            <span>Loading snapshot data...</span>
          </div>
        )}

        {error && (
          <div style={styles.errorContainer}>
            Snapshots unavailable for this import
          </div>
        )}

        {!isLoading && !error && data && (
          <>
            {renderComparisonGrid()}
            {renderDelta()}
            {renderExpandableDetails()}
          </>
        )}
      </div>
    </div>
  );
}
